// Dialog (centered modal), Sheet (side drawer), ConfirmDialog (Radix AlertDialog with optional
// typed confirmation) and an imperative `confirm()` backed by <ConfirmHost/> (mounted by shells).
import { useEffect, useRef, useState, type ReactNode } from 'react'
import * as D from '@radix-ui/react-dialog'
import * as AD from '@radix-ui/react-alert-dialog'
import { TriangleAlert, X } from 'lucide-react'
import { cn } from './cn'
import { Button } from './button'
import { Input } from './input'
import { setLeavePrompt } from '@/lib/hooks'

const overlay = 'fixed inset-0 z-50 bg-overlay backdrop-blur-[2px] data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out'
/** Open on the first field (not the × button) when the dialog holds a form. */
const focusFirstField = (e: Event) => {
  const root = e.target as HTMLElement | null
  const first = root?.querySelector<HTMLElement>('[autofocus], input:not([type="hidden"]):not(:disabled):not([type="checkbox"]):not([type="radio"]), textarea:not(:disabled), [role="combobox"]:not(:disabled)')
  if (first) { e.preventDefault(); first.focus() }
}

const widths = { sm: 'max-w-[400px]', md: 'max-w-[520px]', lg: 'max-w-[680px]', xl: 'max-w-[880px]', full: 'max-w-[calc(100vw-32px)]' } as const

export interface DialogProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Element that opens the dialog (uncontrolled use). */
  trigger?: ReactNode
  title: ReactNode
  description?: ReactNode
  /** Footer actions, right-aligned (Cancel + primary). */
  footer?: ReactNode
  size?: keyof typeof widths
  children?: ReactNode
  /** Hide the × button (forces a footer choice). */
  hideClose?: boolean
  /** Prevent closing on outside click / Esc (e.g. while saving). */
  dismissible?: boolean
  className?: string
  bodyClassName?: string
}

/**
 * Modal dialog with header / scrolling body / footer.
 * `<Dialog open={o} onOpenChange={setO} title="Edit address" footer={<><Button onClick={close}>Cancel</Button><Button variant="primary">Save</Button></>}>…</Dialog>`
 */
export function Dialog({ open, onOpenChange, trigger, title, description, footer, size = 'md', children, hideClose, dismissible = true, className, bodyClassName }: DialogProps) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <D.Trigger asChild>{trigger}</D.Trigger>}
      <D.Portal>
        <D.Overlay className={overlay} />
        <D.Content
          aria-modal="true"
          onOpenAutoFocus={focusFirstField}
          onPointerDownOutside={e => { if (!dismissible) e.preventDefault() }}
          onEscapeKeyDown={e => { if (!dismissible) e.preventDefault() }}
          className={cn(
            'fixed top-1/2 left-1/2 z-50 flex max-h-[calc(100dvh-32px)] w-[calc(100vw-24px)] -translate-x-1/2 -translate-y-1/2 flex-col',
            'rounded-dialog border border-border bg-surface-raised text-text shadow-dialog outline-none',
            'data-[state=open]:animate-dialog-in data-[state=closed]:animate-dialog-out',
            widths[size], className,
          )}
        >
          <div className="flex items-start gap-3 px-5 pt-4 pb-3">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <D.Title className="text-md font-semibold text-text">{title}</D.Title>
              {description ? <D.Description className="text-base text-text-muted">{description}</D.Description> : <D.Description className="sr-only">{typeof title === 'string' ? title : 'Dialog'}</D.Description>}
            </div>
            {!hideClose && (
              <D.Close aria-label="Close" className="-mr-1 grid size-6 shrink-0 place-items-center rounded-control text-text-subtle hover:bg-surface-active hover:text-text focus-visible:outline-2 focus-visible:outline-focus">
                <X className="size-4" />
              </D.Close>
            )}
          </div>
          {children !== undefined && <div className={cn('min-h-0 flex-1 overflow-y-auto px-5 pb-4', bodyClassName)}>{children}</div>}
          {footer && <div className="flex flex-wrap items-center justify-end gap-2 rounded-b-dialog border-t border-border bg-surface-sunken/60 px-5 py-3">{footer}</div>}
        </D.Content>
      </D.Portal>
    </D.Root>
  )
}
export const DialogClose = D.Close

export interface SheetProps extends Omit<DialogProps, 'size'> {
  side?: 'right' | 'left'
  /** Panel width (default md = 440 px). */
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** Render without header chrome (e.g. the mobile nav drawer). */
  bare?: boolean
}
const sheetWidths = { sm: 'sm:max-w-[360px]', md: 'sm:max-w-[440px]', lg: 'sm:max-w-[560px]', xl: 'sm:max-w-[720px]' }

/** Side drawer for detail panes and secondary forms: `<Sheet open={o} onOpenChange={setO} title="Filters">…</Sheet>`. */
export function Sheet({ open, onOpenChange, trigger, title, description, footer, side = 'right', size = 'md', children, hideClose, dismissible = true, bare, className, bodyClassName }: SheetProps) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <D.Trigger asChild>{trigger}</D.Trigger>}
      <D.Portal>
        <D.Overlay className={overlay} />
        <D.Content
          aria-modal="true"
          onOpenAutoFocus={focusFirstField}
          onPointerDownOutside={e => { if (!dismissible) e.preventDefault() }}
          onEscapeKeyDown={e => { if (!dismissible) e.preventDefault() }}
          className={cn(
            'fixed inset-y-0 z-50 flex w-full flex-col bg-surface-raised text-text shadow-dialog outline-none',
            side === 'right' ? 'right-0 border-l border-border data-[state=open]:animate-sheet-in-right data-[state=closed]:animate-sheet-out-right'
              : 'left-0 border-r border-border data-[state=open]:animate-sheet-in-left data-[state=closed]:animate-sheet-out-left',
            bare ? 'max-w-[300px]' : sheetWidths[size], className,
          )}
        >
          {bare ? (
            <>
              <D.Title className="sr-only">{title}</D.Title>
              <D.Description className="sr-only">{typeof title === 'string' ? title : 'Panel'}</D.Description>
              {children}
            </>
          ) : (
            <>
              <div className="flex items-start gap-3 border-b border-border px-5 py-4">
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <D.Title className="text-md font-semibold">{title}</D.Title>
                  {description ? <D.Description className="text-base text-text-muted">{description}</D.Description> : <D.Description className="sr-only">{typeof title === 'string' ? title : 'Panel'}</D.Description>}
                </div>
                {!hideClose && (
                  <D.Close aria-label="Close" className="-mr-1 grid size-6 shrink-0 place-items-center rounded-control text-text-subtle hover:bg-surface-active hover:text-text focus-visible:outline-2 focus-visible:outline-focus">
                    <X className="size-4" />
                  </D.Close>
                )}
              </div>
              <div className={cn('min-h-0 flex-1 overflow-y-auto px-5 py-4', bodyClassName)}>{children}</div>
              {footer && <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3 pb-[max(12px,env(safe-area-inset-bottom))]">{footer}</div>}
            </>
          )}
        </D.Content>
      </D.Portal>
    </D.Root>
  )
}

// ─── Confirm ────────────────────────────────────────────────────────────────────────────

export interface ConfirmOptions {
  title: ReactNode
  description?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Red confirm button + warning icon. */
  destructive?: boolean
  /** The user must type this text (e.g. the store slug or "DELETE") to enable the confirm button. */
  typeToConfirm?: string
  /** Runs on confirm; the dialog shows a spinner and stays open if it throws. */
  onConfirm?: () => unknown | Promise<unknown>
}

export interface ConfirmDialogProps extends ConfirmOptions {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called when the user cancels (Esc, Cancel). */
  onCancel?: () => void
}

/**
 * Confirmation (AlertDialog). For destructive actions set `destructive`, and for irreversible
 * ones add `typeToConfirm`. Prefer the imperative helper: `if (await confirm({...})) doIt()`.
 */
export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel = 'Confirm', cancelLabel = 'Cancel', destructive, typeToConfirm, onConfirm, onCancel }: ConfirmDialogProps) {
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => { if (open) { setTyped(''); setBusy(false); setErr(null) } }, [open])
  const blocked = !!typeToConfirm && typed.trim() !== typeToConfirm
  const run = async () => {
    if (blocked || busy) return
    if (!onConfirm) { onOpenChange(false); return }
    setBusy(true); setErr(null)
    try { await onConfirm(); onOpenChange(false) }
    catch (e) { setErr(e instanceof Error ? e.message : 'Something went wrong.') }
    finally { setBusy(false) }
  }
  return (
    <AD.Root open={open} onOpenChange={o => { if (!busy) { if (!o) onCancel?.(); onOpenChange(o) } }}>
      <AD.Portal>
        <AD.Overlay className={overlay} />
        <AD.Content aria-modal="true" className={cn(
          'fixed top-1/2 left-1/2 z-50 flex w-[calc(100vw-24px)] max-w-[440px] -translate-x-1/2 -translate-y-1/2 flex-col rounded-dialog border border-border bg-surface-raised shadow-dialog outline-none',
          'data-[state=open]:animate-dialog-in data-[state=closed]:animate-dialog-out',
        )}>
          <form onSubmit={e => { e.preventDefault(); void run() }}>
            <div className="flex gap-4 px-5 pt-5 pb-4">
              {destructive && (
                <div className="grid size-9 shrink-0 place-items-center rounded-full bg-danger-soft text-danger-strong"><TriangleAlert className="size-4.5" /></div>
              )}
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <AD.Title className="text-md font-semibold text-text">{title}</AD.Title>
                {description ? <AD.Description className="text-base text-text-muted">{description}</AD.Description> : <AD.Description className="sr-only">Confirm this action</AD.Description>}
                {typeToConfirm && (
                  <div className="mt-2 flex flex-col gap-2">
                    <label className="text-sm text-text-muted" htmlFor="confirm-typed">Type <span className="font-mono font-semibold text-text select-all">{typeToConfirm}</span> to confirm</label>
                    <Input id="confirm-typed" value={typed} onChange={e => setTyped(e.target.value)} autoComplete="off" autoFocus spellCheck={false} />
                  </div>
                )}
                {err && <p role="alert" className="mt-1 text-sm text-danger-strong">{err}</p>}
              </div>
            </div>
            <div className="flex flex-col-reverse gap-2 rounded-b-dialog border-t border-border bg-surface-sunken/60 px-5 py-3 sm:flex-row sm:justify-end">
              <AD.Cancel asChild><Button disabled={busy}>{cancelLabel}</Button></AD.Cancel>
              <Button type="submit" variant={destructive ? 'danger' : 'primary'} loading={busy} disabled={blocked} autoFocus={!typeToConfirm}>{confirmLabel}</Button>
            </div>
          </form>
        </AD.Content>
      </AD.Portal>
    </AD.Root>
  )
}

type Pending = ConfirmOptions & { resolve: (v: boolean) => void; id: number }
let push: ((p: Pending) => void) | null = null
let nextId = 1

const plain = (n: ReactNode): string => (typeof n === 'string' || typeof n === 'number' ? String(n) : '')

/**
 * Promise-based confirmation: `if (!(await confirm({ title: 'Delete 3 products?', destructive: true }))) return`.
 * Needs <ConfirmHost/> (every shell and the kit mount it). Without one it falls back to the browser's
 * own dialogs — still honouring `typeToConfirm` (window.prompt) and awaiting `onConfirm` (resolves
 * false if it throws) — so no caller ever proceeds without the confirmation it asked for.
 */
export function confirm(opts: ConfirmOptions): Promise<boolean> {
  if (push) return new Promise(resolve => push!({ ...opts, resolve, id: nextId++ }))
  return nativeConfirm(opts)
}

async function nativeConfirm(opts: ConfirmOptions): Promise<boolean> {
  if (typeof window === 'undefined') return false
  const text = [plain(opts.title) || 'Are you sure?', plain(opts.description)].filter(Boolean).join('\n\n')
  let ok: boolean
  if (opts.typeToConfirm) {
    const typed = window.prompt(`${text}\n\nType "${opts.typeToConfirm}" to confirm.`, '')
    ok = typed !== null && typed.trim() === opts.typeToConfirm
  } else ok = window.confirm(text)
  if (!ok) return false
  if (!opts.onConfirm) return true
  try { await opts.onConfirm(); return true }
  catch (e) { window.alert(e instanceof Error && e.message ? e.message : 'Something went wrong.'); return false }
}

/** Renders queued confirm() dialogs and installs the "unsaved changes" prompt. Mount once. */
export function ConfirmHost() {
  const [queue, setQueue] = useState<Pending[]>([])
  const current = queue[0]
  const resolved = useRef(false)
  useEffect(() => {
    push = p => setQueue(q => [...q, p])
    setLeavePrompt(message => confirm({ title: 'Discard unsaved changes?', description: message, confirmLabel: 'Discard changes', cancelLabel: 'Keep editing', destructive: true }))
    return () => { push = null; setLeavePrompt(null) }
  }, [])
  useEffect(() => { resolved.current = false }, [current])
  if (!current) return null
  const finish = (v: boolean) => {
    if (resolved.current) return
    resolved.current = true
    current.resolve(v)
    setQueue(q => q.slice(1))
  }
  return (
    <ConfirmDialog
      // A fresh instance per request: the typed confirmation, spinner and error never carry over
      // from the previous dialog in the queue.
      key={current.id}
      {...current}
      open
      onOpenChange={o => { if (!o) finish(true) }}
      onCancel={() => finish(false)}
      onConfirm={async () => { if (current.onConfirm) await current.onConfirm() }}
    />
  )
}
